// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import Head from 'next/head';
import { cloudinaryUrl } from '@realadvisor/cloudinary';
import { useLocale } from '../../hooks/locale';
import { useRouter } from '../../controls/router';
import { useUserData } from '../../hooks/user-data';
import { translatePropertyType } from '../translate/propertyTypes';
import type { AggregatesListingPageMeta_listing$key } from './__generated__/AggregatesListingPageMeta_listing.graphql';
import type { AggregatesListingPageMeta_page$key } from './__generated__/AggregatesListingPageMeta_page.graphql';

type Props = {|
  listing: AggregatesListingPageMeta_listing$key,
  page: ?AggregatesListingPageMeta_page$key,
|};

export const AggregatesListingPageMeta = (props: Props): React.Node => {
  const {
    t,
    language,
    numberLocale,
    interpolate,
    currency: fallbackCurrency,
  } = useLocale();
  const router = useRouter();
  const { origin } = useUserData();

  const listing = useFragment(
    graphql`
      fragment AggregatesListingPageMeta_listing on AggregatesListing {
        title
        description
        offerType
        propertyType
        propertyMainType
        lat
        lng
        postcode
        subLocality
        route
        streetNumber
        livingSurface
        numberOfRooms
        numberOfBathrooms
        salePrice
        grossRentMonthly
        currency
        images(first: 100) {
          bucketName
          fileName
        }
      }
    `,
    props.listing,
  );

  const page = useFragment(
    graphql`
      fragment AggregatesListingPageMeta_page on CMSPage {
        title
        description
      }
    `,
    props.page,
  );

  const {
    title,
    currency,
    description,
    images,
    grossRentMonthly,
    livingSurface,
    numberOfRooms,
    numberOfBathrooms,
    postcode,
    propertyMainType,
    lat,
    lng,
    propertyType,
    route,
    salePrice,
    streetNumber,
    subLocality,
    offerType,
  } = listing;

  const { propertyTypeSingularCapital, propertyTypeSingular } =
    translatePropertyType(t, propertyType ?? propertyMainType ?? '');

  const formatCurrency = val =>
    val != null
      ? numberLocale.format(val, {
          style: 'currency',
          currency: currency ?? fallbackCurrency,
          currencyDisplay: 'symbol',
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        })
      : '';

  const formattedAddress = [
    [
      route != null ? [route, streetNumber].filter(Boolean).join(' ') : null,
      postcode,
    ]
      .filter(x => x != null)
      .join(', '),
    subLocality ?? '-',
  ].join(' ');

  const realPrice = offerType === 'buy' ? salePrice : grossRentMonthly;

  const formattedPrice =
    offerType === 'rent'
      ? grossRentMonthly != null
        ? [formatCurrency(grossRentMonthly), t('month')].join('/')
        : t('onRequest')
      : salePrice != null
      ? formatCurrency(salePrice)
      : t('onRequest');

  const generatedTitle =
    offerType === 'buy'
      ? t('propertyTypeCardTitle_sell', {
          propertyType: propertyTypeSingularCapital,
        })
      : t('propertyTypeCardTitle_rent', {
          propertyType: propertyTypeSingularCapital,
        });

  const imgUrls = (images || []).map(image =>
    cloudinaryUrl(
      { f: 'jpg' },
      { bucketName: image.bucketName, fileName: image.fileName },
    ),
  );

  const firstImage = imgUrls.length > 0 ? imgUrls[0] : null;

  const titleOptions = {
    generatedTitle,
    listingTitle: title ?? '',
    address: formattedAddress,
    formattedPrice,
    description: (description || '').slice(0, 120),
    features: [
      numberOfRooms != null
        ? t('countRooms', {
            count: numberOfRooms ?? 0,
          })
        : '',
      livingSurface != null ? `${livingSurface}m²` : '',
    ]
      .filter(Boolean)
      .join(', '),
    offerType: offerType === 'rent' ? t('forRent') : t('forSale'),
    propertyTypeSingularCapital,
    propertyTypeSingular,
    numberOfPhotos: t('countPhotos', { count: imgUrls.length }),
  };

  const translatedTitle = interpolate(page?.title ?? '', titleOptions);
  const shortDescription = interpolate(
    page?.description ?? '',
    titleOptions,
  ).substring(0, 160);

  const path = router.asPath;

  return (
    <Head>
      <title>{translatedTitle}</title>
      <meta name="robots" content="noindex, noarchive, nofollow" />
      <meta name="ICBM" content={`${lat ?? ''}, ${lng ?? ''}`} />
      <meta name="geo.position" content={`${lat ?? ''}, ${lng ?? ''}`} />
      <meta
        name="geo.placename"
        content={`${postcode ?? ''} ${subLocality ?? ''}`}
      />
      <meta name="geo.region" content={postcode ?? ''} />
      <meta name="description" content={shortDescription} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={translatedTitle} />
      <meta name="twitter:description" content={shortDescription} />
      <meta name="twitter:text:title" content={translatedTitle} />
      <meta name="twitter:text:description" content={description} />
      <meta name="twitter:image" content={firstImage} />
      <meta name="twitter:image:alt" content={description} />
      <meta name="twitter:text:description_simple" content={translatedTitle} />
      <meta name="twitter:image:src" content={firstImage} />
      <meta name="twitter:text:street_address" content={formattedAddress} />
      <meta name="twitter:text:city" content={subLocality} />
      <meta name="twitter:text:zip" content={postcode} />
      <meta name="twitter:text:price" content={formattedPrice} />
      <meta name="twitter:text:rooms" content={numberOfRooms} />
      <meta name="twitter:text:baths" content={numberOfBathrooms} />
      <meta name="twitter:url" content={`${origin}${path}`} />
      <meta property="og:title" content={translatedTitle} />
      <meta property="og:type" content="og:product" />
      <meta property="og:url" content={`${origin}${path}`} />
      <meta property="product:price:amount" content={realPrice} />
      <meta property="product:price:currency" content={currency} />
      <meta property="og:site_name" content="RealAdvisor" />
      <meta property="og:description" content={shortDescription} />
      <meta property="og:locale" content={language} />
      <meta property="og:latitude" content={lat ?? ''} />
      <meta property="og:longitude" content={lng ?? ''} />
      <meta
        name="viewport"
        content="width=device-width, height=device-height, initial-scale=1"
      />
      {imgUrls.map((url, i) => (
        <React.Fragment key={i}>
          <meta name={`twitter:image:photo${i}`} content={url} />
          <meta name="og:image" content={url} />
          <meta name="og:image:secure_url" content={url} />
        </React.Fragment>
      ))}
      <meta name="apple-itunes-app" content="app-id=6504883593" />
    </Head>
  );
};
